import React, { useEffect, useRef } from "react";
import { useExcalidrawAPI } from "@excalidraw/excalidraw";

import {
  getScene,
  getSceneContent,
  getSharedScene,
  putSceneContent,
} from "../lib/plbxApi";
import { PlbxSaveHotkey } from "./PlbxSaveHotkey";

/**
 * Fill in fields Excalidraw expects on every element. Scenes built via the
 * MCP server or external scripts often omit "boring" defaults (boundElements,
 * groupIds, isDeleted, …). The stock renderer happily reads them as
 * `undefined` and then crashes inside `boundElements.forEach`. We patch each
 * element to the documented shape before handing it to updateScene.
 */
function normalizeElements(els: any[]): any[] {
  const now = Date.now();
  return (els || []).filter(Boolean).map((raw) => {
    const e: any = { ...raw };
    if (e.angle == null) e.angle = 0;
    if (!Array.isArray(e.boundElements)) e.boundElements = [];
    if (e.frameId === undefined) e.frameId = null;
    if (!Array.isArray(e.groupIds)) e.groupIds = [];
    if (e.isDeleted == null) e.isDeleted = false;
    if (e.link === undefined) e.link = null;
    if (e.locked == null) e.locked = false;
    if (typeof e.opacity !== "number") e.opacity = 100;
    if (e.updated == null) e.updated = now;
    if (e.version == null) e.version = 1;
    if (e.versionNonce == null) {
      e.versionNonce = Math.floor(Math.random() * 2 ** 31);
    }
    if (e.seed == null) e.seed = Math.floor(Math.random() * 2 ** 31);
    if (e.strokeColor == null) e.strokeColor = "#1e1e1e";
    if (e.backgroundColor == null) e.backgroundColor = "transparent";
    if (e.fillStyle == null) e.fillStyle = "solid";
    if (typeof e.strokeWidth !== "number") e.strokeWidth = 2;
    if (e.strokeStyle == null) e.strokeStyle = "solid";
    if (e.roughness == null) e.roughness = 1;
    if (e.roundness === undefined) e.roundness = null;
    if (e.index == null) e.index = "a0";
    // Text-specific
    if (e.type === "text") {
      if (e.containerId === undefined) e.containerId = null;
      if (e.lineHeight == null) e.lineHeight = 1.25;
      if (e.verticalAlign == null) e.verticalAlign = "middle";
      if (e.textAlign == null) e.textAlign = "left";
      if (e.fontFamily == null) e.fontFamily = 1;
      if (e.fontSize == null) e.fontSize = 16;
      if (e.text == null) e.text = "";
      if (e.originalText == null) e.originalText = e.text;
      if (e.autoResize == null) e.autoResize = true;
    }
    // Arrow/line-specific
    if (e.type === "arrow" || e.type === "line") {
      if (!Array.isArray(e.points))
        e.points = [
          [0, 0],
          [(e.width as number) || 0, (e.height as number) || 0],
        ];
      if (e.startBinding === undefined) e.startBinding = null;
      if (e.endBinding === undefined) e.endBinding = null;
      if (e.lastCommittedPoint === undefined) e.lastCommittedPoint = null;
      if (e.startArrowhead === undefined) e.startArrowhead = null;
      if (e.endArrowhead === undefined)
        e.endArrowhead = e.type === "arrow" ? "arrow" : null;
      if (e.elbowed == null) e.elbowed = false;
    }
    return e;
  });
}

const AUTOSAVE_DEBOUNCE_MS = 1000;

type SaveStatus = "saving" | "saved" | "error";

/**
 * Publish autosave status to the board-title chip (PlbxBoardTitle), which lives
 * outside Excalidraw's Jotai provider. We cannot drive a React state from here:
 * PlbxBoardSync consumes useExcalidrawAPI, and a setState after the async
 * hydrate re-renders inside the scoped provider and crashes useAtomValue
 * (see header docblock). A window CustomEvent is the side channel. The mirror
 * on window.__plbxSaveStatus lets the chip read the latest value on mount even
 * if it attached its listener after an event already fired.
 */
function emitSaveStatus(status: SaveStatus): void {
  (window as any).__plbxSaveStatus = status;
  window.dispatchEvent(
    new CustomEvent("plbx:autosave", { detail: { status } }),
  );
}

/**
 * Mounts inside ExcalidrawWrapper (patch 03). Handles three modes:
 *
 * 1. Team / authed:
 *    - localStorage `plbx_pending_board` is set by /boards click.
 *    - Fetch + hydrate via authed REST. Wire autosave.
 *
 * 2. Public read-only viewer (share.draw.plbx.ai):
 *    - URL hash `#board=<token>`.
 *    - Fetch via public /api/v1/share/{token} (no auth). Hydrate; no autosave.
 *
 * 3. Stock /:  no flag → no-op, native localStorage canvas behaviour.
 *
 * Why hash (not query): Excalidraw stock parses query string at init for its
 * own scene-data URLs. Adding `?board=` blanks the canvas. Hash is safe.
 *
 * Why ref-only (no useState): setState after async fetch re-renders inside
 * Excalidraw's scoped Jotai Provider and crashes useAtomValue.
 */
export const PlbxBoardSync: React.FC = () => {
  const excalidrawAPI = useExcalidrawAPI();
  const wiredRef = useRef(false);

  useEffect(() => {
    if (!excalidrawAPI || wiredRef.current) return;

    // Open priority:
    //   1. `plbx_pending_board` — one-shot handoff from /boards click.
    //   2. `plbx_active_board` — sticky last-opened board (survives reload).
    //   3. `#board=<token>` — public share viewer mode.
    // None of those → stock blank canvas, no autosave wiring.
    const hash = window.location.hash;
    const shareToken = hash.startsWith("#board=") ? hash.slice(7) : null;
    // Guest in live-collab room (`#room=<id>,<key>`). Excalidraw's own Collab
    // module owns the canvas; we must NOT hydrate from our backend (would
    // overwrite the room) or wire autosave (would PUT a board the guest does
    // not own). Bail before reading localStorage so a stale `plbx_active_board`
    // from the guest's own past session does not leak in either.
    if (hash.startsWith("#room=")) return;

    const handoff = localStorage.getItem("plbx_pending_board");
    const sticky = localStorage.getItem("plbx_active_board");
    const pendingBoard = handoff || sticky;

    if (!pendingBoard && !shareToken) return;
    wiredRef.current = true;

    let unsub: (() => void) | undefined;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let lastFp: string | null = null;
    // Assigned inside the async IIFE (team branch) so the cleanup closure can
    // flush + detach the close-handlers without reaching into IIFE scope.
    let flush: (() => void) | undefined;
    let onVisibility: (() => void) | undefined;
    let onPageHide: (() => void) | undefined;

    (async () => {
      // Defer one frame so Excalidraw's own initial render finishes;
      // updateScene during initial mount blanks the canvas.
      await new Promise((r) => requestAnimationFrame(() => r(null)));

      try {
        if (pendingBoard) {
          localStorage.removeItem("plbx_pending_board");
          (window as any).__plbxBoardId = pendingBoard;
          let meta;
          try {
            meta = await getScene(pendingBoard);
          } catch (err: any) {
            // Sticky id no longer valid — clear it and abort hydrate.
            if (err?.statusCode === 404) {
              localStorage.removeItem("plbx_active_board");
              delete (window as any).__plbxBoardId;
            }
            throw err;
          }
          // Pin the active board so the next reload restores it without an
          // extra click through /boards.
          localStorage.setItem("plbx_active_board", pendingBoard);
          (window as any).__plbxBoardName = meta.name;
          (window as any).__plbxBoardWorkspaceSlug = meta.workspaceSlug;
          (window as any).__plbxBoardWorkspaceId = meta.workspaceId;
          const content = await getSceneContent(pendingBoard);
          const teamEls = normalizeElements((content.elements as any) ?? []);
          excalidrawAPI.updateScene({ elements: teamEls });
          if (content.files && Object.keys(content.files).length > 0) {
            excalidrawAPI.addFiles(
              Object.values(content.files).filter(Boolean) as any,
            );
          }
          // Fit camera to content — default scrollX/Y leaves the board
          // outside the viewport.
          const validTeam = teamEls.filter(
            (e: any) => Number.isFinite(e?.width) && Number.isFinite(e?.height),
          );
          if (validTeam.length) {
            await new Promise((r) =>
              requestAnimationFrame(() =>
                requestAnimationFrame(() => r(null)),
              ),
            );
            excalidrawAPI.scrollToContent(validTeam, {
              fitToViewport: true,
              animate: false,
            });
          }

          // Wire autosave only after hydrate so the initial updateScene
          // does not trigger an immediate PUT.
          //
          // Latest scene snapshot + dirty flag live in closures (not refs) —
          // doSave/flush read them on demand. The debounce alone loses progress
          // if the page closes within the debounce window, so doSave also runs
          // from a close-handler flush with keepalive.
          let latestElements: readonly any[] = teamEls;
          let latestFiles: Record<string, any> = (content.files as any) ?? {};
          let dirty = false;

          const doSave = (keepalive: boolean) => {
            if (!dirty) return;
            dirty = false;
            emitSaveStatus("saving");
            putSceneContent(
              pendingBoard,
              {
                type: "excalidraw",
                version: 2,
                source: window.location.origin,
                elements: latestElements as any[],
                appState: {} as any,
                files: latestFiles as any,
              },
              keepalive ? { keepalive: true } : undefined,
            )
              .then(() => {
                // Edits during the in-flight PUT re-set dirty — keep "saving".
                emitSaveStatus(dirty ? "saving" : "saved");
              })
              .catch((err) => {
                dirty = true; // not persisted — retry on next change / flush
                emitSaveStatus("error");
                console.warn("[plbx] autosave failed", err);
              });
          };

          // Persist any pending edits immediately (tab hidden / page unload).
          flush = () => {
            if (timer) {
              clearTimeout(timer);
              timer = null;
            }
            if (dirty) doSave(true);
          };

          unsub = excalidrawAPI.onChange((elements, _appState, files) => {
            const fp = `${elements.length}:${
              elements.length
                ? (elements[elements.length - 1] as any).version
                : 0
            }:${elements.reduce(
              (acc: number, el: any) => acc + (el.version || 0),
              0,
            )}`;
            if (fp === lastFp) return;
            lastFp = fp;

            latestElements = elements;
            latestFiles = files as Record<string, any>;
            dirty = true;
            emitSaveStatus("saving");

            if (timer) clearTimeout(timer);
            timer = setTimeout(() => {
              timer = null;
              doSave(false);
            }, AUTOSAVE_DEBOUNCE_MS);
          });

          // Board is loaded and in sync with the server.
          emitSaveStatus("saved");

          // Flush on tab-hide and on page teardown. visibilitychange→hidden +
          // pagehide is the modern reliable pair (beforeunload is flaky on
          // mobile / bfcache). keepalive keeps the PUT alive past unload.
          onVisibility = () => {
            if (document.visibilityState === "hidden") flush?.();
          };
          onPageHide = () => flush?.();
          document.addEventListener("visibilitychange", onVisibility);
          window.addEventListener("pagehide", onPageHide);
        } else if (shareToken) {
          // Read-only viewer. Hydrate + center on content. View-mode is set
          // statically via the <Excalidraw viewModeEnabled> prop (patched in).
          const data = await getSharedScene(shareToken);
          const elements = normalizeElements((data.elements as any) ?? []);
          const saved = (data.appState as any) ?? {};
          // Apply saved viewport — Excalidraw default scrollX/Y/zoom may
          // place the camera far from saved-content coordinates.
          const appStatePatch: any = {};
          if (Number.isFinite(saved.scrollX)) appStatePatch.scrollX = saved.scrollX;
          if (Number.isFinite(saved.scrollY)) appStatePatch.scrollY = saved.scrollY;
          if (saved.zoom?.value && Number.isFinite(saved.zoom.value)) {
            appStatePatch.zoom = saved.zoom;
          }
          excalidrawAPI.updateScene(
            Object.keys(appStatePatch).length
              ? { elements, appState: appStatePatch }
              : { elements },
          );
          if (data.files && Object.keys(data.files).length > 0) {
            excalidrawAPI.addFiles(
              Object.values(data.files).filter(Boolean) as any,
            );
          }
          // Skip scrollToContent for malformed elements (no width/height) —
          // would set NaN zoom. Real Excalidraw scenes always have these.
          const valid = elements.filter(
            (e: any) => Number.isFinite(e?.width) && Number.isFinite(e?.height),
          );
          if (valid.length) {
            // Excalidraw scrollToContent silently no-ops in viewMode after
            // imperative updateScene. Workaround: dispatch the same keyboard
            // shortcut Excalidraw binds to "fit to content" (Shift+1) on the
            // canvas, which works regardless of viewMode.
            await new Promise((r) =>
              requestAnimationFrame(() =>
                requestAnimationFrame(() => r(null)),
              ),
            );
            const canvas = document.querySelector(
              ".excalidraw__canvas",
            ) as HTMLCanvasElement | null;
            (canvas ?? document.body).dispatchEvent(
              new KeyboardEvent("keydown", {
                key: "1",
                code: "Digit1",
                shiftKey: true,
                bubbles: true,
                cancelable: true,
              }),
            );
          }
        }
      } catch (e) {
        console.warn("[plbx] hydrate failed", e);
      }
    })();

    return () => {
      // Unmount (e.g. SPA nav away from the board) won't fire pagehide —
      // flush pending edits here too before tearing down.
      flush?.();
      if (timer) clearTimeout(timer);
      unsub?.();
      if (onVisibility) {
        document.removeEventListener("visibilitychange", onVisibility);
      }
      if (onPageHide) window.removeEventListener("pagehide", onPageHide);
    };
  }, [excalidrawAPI]);

  // Always-mounted global Cmd/Ctrl+S handler (works regardless of menu state).
  return <PlbxSaveHotkey />;
};

export default PlbxBoardSync;
